import { useMemo, useState } from 'react';
import { Bookmark, BookmarkX, Loader2, Search, SearchX, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/cn';
import { getErrorMessage } from '@/lib/getErrorMessage';
import type { ChannelData, SavedMessage } from '@/api/services/channels.service';
import { useSavedMessages } from '../hooks/useChannels';
import {
  formatResultDate,
  fullName,
  searchTerms,
  senderOf,
  toPlainText,
} from '../utils/messageResults';
import { Kbd, MessageResultContent, ResultSkeleton, StatePanel } from './MessageResultParts';

const QUIET_BUTTON_CLASS =
  'rounded-md border border-slate-300 px-2.5 py-1 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-primary/30 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/[0.06]';

export interface SavedMessagesDialogProps {
  open: boolean;
  onClose: () => void;
  /** Active and archived channels, to label each saved message's conversation. */
  channels: ChannelData[];
  onSelect: (message: SavedMessage) => void;
  onUnsave: (message: SavedMessage) => void;
}

/**
 * The user's bookmarks across every channel and DM they can still read. The
 * filter only narrows what is loaded; older saves come in with "Load more".
 */
export function SavedMessagesDialog({
  open,
  onClose,
  channels,
  onSelect,
  onUnsave,
}: SavedMessagesDialogProps): React.ReactElement {
  const [filter, setFilter] = useState('');
  const saved = useSavedMessages(open);
  const items = useMemo(() => saved.data?.pages.flatMap((page) => page.items) ?? [], [saved.data]);
  const channelById = useMemo(
    () => new Map(channels.map((channel) => [channel._id, channel])),
    [channels]
  );
  const terms = useMemo(() => searchTerms(filter, 1), [filter]);
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const sender = senderOf(item);
      const haystack = [
        toPlainText(item.content),
        sender ? fullName(sender) : '',
        channelById.get(item.channelId)?.name ?? '',
        ...item.attachments.map((file) => file.name),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [channelById, filter, items]);

  const renderBody = (): React.ReactNode => {
    if (saved.isLoading) return <ResultSkeleton />;
    if (saved.isError) {
      return (
        <StatePanel icon={SearchX} title="Couldn’t load saved messages">
          <p>{getErrorMessage(saved.error)}</p>
          <button
            type="button"
            onClick={() => void saved.refetch()}
            className={cn(QUIET_BUTTON_CLASS, 'mt-3')}
          >
            Try again
          </button>
        </StatePanel>
      );
    }
    if (items.length === 0 && !saved.hasNextPage) {
      return (
        <StatePanel icon={Bookmark} title="No saved messages yet">
          Hover over any message and choose Save to keep it here for later.
        </StatePanel>
      );
    }
    return (
      <div className="px-1.5 py-1">
        {shown.length === 0 && (
          <p className="px-3 py-6 text-center text-[12px] text-slate-500 dark:text-gray-400">
            No loaded saved messages match “{filter.trim()}”
            {saved.hasNextPage ? '. Load more to search older saves.' : '.'}
          </p>
        )}
        {shown.map((item) => (
          <div
            key={item._id}
            className="group/saved relative rounded-md transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.03]"
          >
            <button
              type="button"
              onClick={() => onSelect(item)}
              className="flex w-full gap-3 rounded-md px-2.5 py-2 pr-10 text-left focus-visible:bg-slate-100 dark:focus-visible:bg-white/[0.06]"
            >
              <MessageResultContent
                message={item}
                channel={channelById.get(item.channelId)}
                terms={terms}
              />
            </button>
            <div className="pointer-events-none absolute bottom-2 right-3 text-[10px] text-slate-400 group-hover/saved:hidden dark:text-gray-500">
              <span title={`Saved ${formatResultDate(item.savedAt)}`}>
                <Bookmark className="h-3 w-3 fill-current" />
              </span>
            </div>
            <button
              type="button"
              onClick={() => onUnsave(item)}
              className="absolute bottom-1.5 right-2 hidden rounded p-1 text-slate-400 transition-colors hover:bg-slate-200/70 hover:text-slate-700 focus-visible:flex group-hover/saved:flex dark:hover:bg-white/10 dark:hover:text-gray-200 max-sm:flex"
              aria-label="Remove from saved"
              title="Remove from saved"
            >
              <BookmarkX className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {saved.hasNextPage && (
          <div className="flex justify-center py-2">
            <button
              type="button"
              onClick={() => void saved.fetchNextPage()}
              disabled={saved.isFetchingNextPage}
              className={cn(QUIET_BUTTON_CLASS, 'inline-flex items-center gap-1.5')}
            >
              {saved.isFetchingNextPage && <Loader2 className="h-3 w-3 animate-spin" />}
              Load more
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        data-message-search=""
        showCloseButton={false}
        aria-describedby={undefined}
        // Must not also reach the Channels full-screen shortcut on window.
        onEscapeKeyDown={(event) => event.stopPropagation()}
        className={cn(
          'flex flex-col sm:top-[10%] sm:max-w-2xl sm:translate-y-0',
          'border-slate-200 dark:border-white/10 dark:bg-gray-900',
          'max-sm:inset-0 max-sm:h-[100dvh] max-sm:max-h-none max-sm:w-full max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0',
          'sm:max-h-[min(80dvh,640px)]'
        )}
      >
        <DialogTitle className="sr-only">Saved messages</DialogTitle>

        <div className="flex shrink-0 items-center gap-2.5 border-b border-slate-200 px-4 dark:border-white/10">
          <Bookmark className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
          <span className="shrink-0 text-[13px] font-semibold text-slate-800 dark:text-gray-100">
            Saved messages
          </span>
          <div className="relative ml-2 min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Filter saved…"
              aria-label="Filter saved messages"
              autoComplete="off"
              spellCheck={false}
              className="h-8 w-full rounded-md border border-slate-300 bg-white pl-8 pr-7 text-[12px] text-slate-700 placeholder:text-slate-400 outline-none transition-colors focus:border-primary/50 focus:ring-1 focus:ring-primary/20 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-200 dark:placeholder:text-gray-500"
            />
            {filter && (
              <button
                type="button"
                onClick={() => setFilter('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label="Clear filter"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-1.5 py-1 text-[12px] font-medium text-slate-500 hover:text-slate-800 focus-visible:text-slate-800 dark:text-gray-400 dark:hover:text-white sm:hidden"
          >
            Close
          </button>
          <span className="hidden py-3.5 sm:inline-flex">
            <Kbd>esc</Kbd>
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto chat-scroll">{renderBody()}</div>

        <div className="flex shrink-0 items-center border-t border-slate-200 px-4 py-2 text-[11px] text-slate-400 dark:border-white/10 dark:text-gray-500">
          {items.length > 0
            ? `${items.length}${saved.hasNextPage ? '+' : ''} saved · only you can see these`
            : 'Only you can see your saved messages'}
        </div>
      </DialogContent>
    </Dialog>
  );
}

SavedMessagesDialog.displayName = 'SavedMessagesDialog';
